import { Permission } from '@act/shared/authorization';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import { memo } from 'react';
import { HtmlDisplayerWithHyperlinks } from 'react-components/components/HtmlDisplayerWithHyperlinks';
import useInteractionsDialog from 'react-components/dialog-forms/hooks/use-interactions-dialog';
import useMemberStatusChangeRequestDialog from 'react-components/dialog-forms/hooks/use-member-status-change-request-dialog';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { selectSelectedMember } from 'redux/slices/memberSlice';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import {
  messagesAppSelectors,
  updateMessageNoFollowUpNeeded,
} from 'redux/slices/messagesSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { utilityAppSelectors } from 'redux/utils/selectors/utility-selectors';
import styled from 'styled-components';
import InfoCard, {
  AdditionalFeedCardActions,
} from 'toolkit/info-card/info-card';
import { SmartContentDisplay } from '@act/storybook';
import FilePreviewer from 'toolkit/media/file-previewer/file-previewer';
import { Button, TaggableChip } from '@act/storybook';
import { phoneFormatter } from 'utilities/formatters/phone-formatter';
import { twilioSmsErrorMap } from 'redux/types/shared-types';
import { Tooltip } from '@mui/material';
import { MessageDto } from '@act/shared/data-transfer-objects';

const FilePreviewerDiv = styled.div`
  height: 30px;
  margin: 5px 0px;
`;

const getMessageType = (message: MessageDto) => {
  let messageType = 'Outgoing text';
  if (message.incoming) {
    messageType = 'Incoming text';
  }
  if (message.wasSentByCronJob) {
    messageType = 'Outgoing scheduled text';
  }
  return messageType;
};

const FeedMessageCardComponent = ({
  isLastEventItem,
  messageId,
  shouldHighlight,
}: {
  messageId: number;
  isLastEventItem: boolean;
  shouldHighlight: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { createMessage } = useInteractionsDialog();
  const openMemberStatusChangeRequestDialog =
    useMemberStatusChangeRequestDialog();

  const message = messagesAppSelectors.useSelectMessageById(messageId);
  const member = membersAppSelectors.useSelectMemberById(message?.subjectId);
  const author = utilityAppSelectors.selectUserOrMemberById(message?.userId);

  // TODO: I don't like using this
  // because it assumes you are on the correct user's member page!!
  const selectedMember = useAppSelector(selectSelectedMember);

  // this is a little bit of a logic cheat, if message is outgoing,
  // we dont want any admin powers on status changes
  const isAdmin =
    message?.incoming &&
    actingUserAppSelectors.useUserHasPermission(
      Permission.CAN_ACCESS_ADMIN_TOOLS
    );

  // If message doesn't exist (e.g., after deletion), don't render the component
  if (!message) {
    return null;
  }

  const updateNoFollowUp = () => {
    dispatch(
      updateMessageNoFollowUpNeeded({
        body: {
          noFollowUpNeeded: true,
        },
        params: {
          messageId: message.id,
        },
      })
    );
  };

  const statusChips = [];
  const menuItems: AdditionalFeedCardActions[] = [];
  let secondaryOption: React.ReactElement;

  if (isLastEventItem) {
    secondaryOption = (
      <Button
        debounced
        variant="text"
        onClick={() =>
          createMessage({ user: member, phoneNumber: message.smsPhoneNumber })
        }
      >
        Text now
      </Button>
    );

    if (message.userId === message.subjectId && !message.noFollowUpNeeded) {
      menuItems.push({
        title: 'No follow-up needed',
        details: 'We do not need to text user back',
        handler: updateNoFollowUp,
      });
    }
  }

  let shouldShowStatusChangeRequest = false;

  if (message.incoming) {
    // Interaction within 7 days
    shouldShowStatusChangeRequest =
      DateTime.now().minus({ days: 7 }).toMillis() < message.createdAt;

    if (selectedMember?.memberStage?.key === 'outreach') {
      shouldShowStatusChangeRequest = true;
    }
  }

  if (message.smsStatus === 'failed' || message.smsStatus === 'undelivered') {
    let tooltipText = 'Failed to send message';
    const errorMessage =
      twilioSmsErrorMap[message.smsErrorCode]?.message ||
      'Unknown error.  Please contact support';
    if (message.smsErrorCode) {
      tooltipText = `${message.smsErrorCode}: ${errorMessage}`;
    }

    statusChips.push(
      <Tooltip title={tooltipText} arrow={true} placement={'bottom-start'}>
        <div>
          <TaggableChip isTag color="error" label="Failed" />
        </div>
      </Tooltip>
    );
  }

  if (isAdmin || shouldShowStatusChangeRequest) {
    menuItems.push({
      title: 'Request status change',
      icon: shouldShowStatusChangeRequest ? null : (
        <AdminPanelSettingsIcon fontSize="small" />
      ),
      details: 'Request status change',
      handler: () => {
        void openMemberStatusChangeRequestDialog({
          memberId: member.id,
          interactionId: message.interactionId,
          effectiveAt: message.createdAt,
        });
      },
    });
  }

  const messageType = getMessageType(message);

  return (
    <InfoCard
      subject={messageType}
      variant="message"
      startTime={message.sentAt}
      menuItems={menuItems}
      statusChips={statusChips}
      secondaryOption={secondaryOption}
      shouldHighlight={shouldHighlight}
    >
      <>
        <Typography variant="labelLarge" component="p">
          {author?.name || 'Unknown'} wrote:
        </Typography>
        <Typography variant="bodyMedium" component="p">
          {phoneFormatter.stringToPretty(message?.smsPhoneNumber)}
        </Typography>
        {message.text && (
          <SmartContentDisplay>
            <HtmlDisplayerWithHyperlinks note={message.text} />
          </SmartContentDisplay>
        )}

        {message.fileIds?.length > 0 &&
          message.fileIds.map((fi, index) => (
            <FilePreviewerDiv key={`file-previewer-${index}`}>
              <FilePreviewer filedId={fi} />
            </FilePreviewerDiv>
          ))}
      </>
    </InfoCard>
  );
};

// Memoize to prevent re-renders when props haven't changed
const FeedMessageCard = memo(FeedMessageCardComponent);

export default FeedMessageCard;
